// Pure helpers for the Sources page. Extracted so they can be unit
// tested without mounting the full Redux + UploadManager + Router
// stack the page itself needs.

// Status → chip styling map for the per-product compliance pills
// shown next to each source row. All four verdict states use the
// `filled` variant so they read as "result"; the not-yet-checked CTA
// chip uses `outlined` so it reads as "action."
export const COMPLIANCE_STATUS_MAP = {
  pass:        { color: 'success',  variant: 'filled', label: '✓' },
  warn:        { color: 'warning',  variant: 'filled', label: '!' },
  fail:        { color: 'error',    variant: 'filled', label: '✗' },
  needs_input: { color: 'info',     variant: 'filled', label: '?' },
};

// Single-pill summary color (used by the QC drawer header chip and
// some legacy callers). Fallback to "info" for unknown values so a
// stale sidecar with a future-added verdict doesn't crash the page.
export function complianceChipColor(status) {
  if (status === 'pass') return 'success';
  if (status === 'warn') return 'warning';
  if (status === 'fail') return 'error';
  return 'info';
}

// Whether to show the FFEC/FFMC end-credits section in the upload
// guide. The markers are a DCP-only requirement — operators working
// the watermarks side don't need to think about them. Returning
// `true` when activeProduct is null (workspace hasn't picked yet)
// keeps the content for the ambiguous case.
export function shouldShowFfecSection(activeProduct) {
  return activeProduct !== 'press_review';
}

// Count of upload tasks in `done` state. Used to detect "an upload
// just finished" so the source list can auto-refresh. Pulled out of
// the page so the increment-detection hook is easy to test.
export function countDoneTasks(tasks) {
  if (!Array.isArray(tasks)) return 0;
  return tasks.filter((t) => t && t.status === 'done').length;
}

// Upload guide dismissal — persisted in localStorage so an
// experienced operator who's uploaded 50 sources isn't forced
// through 700 words and 4 confirmation checkboxes every time.
// First upload still gates on the guide; once acknowledged + the
// "don't show every time" box is ticked, future uploads skip
// straight to the file picker (the guide is still reachable from
// a "Show upload guide" link in the page header).
export const GUIDE_DISMISSED_KEY = 'sources_guide_dismissed';

export function isGuideDismissed() {
  // SSR / non-browser safety — happens during vitest jsdom boot
  // and would otherwise need a try/catch at every callsite.
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(GUIDE_DISMISSED_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setGuideDismissed(dismissed) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (dismissed) window.localStorage.setItem(GUIDE_DISMISSED_KEY, '1');
    else window.localStorage.removeItem(GUIDE_DISMISSED_KEY);
  } catch (_e) {
    // localStorage can throw under quota / privacy mode; the guide
    // just keeps showing on every upload, which is the safe default.
  }
}

// Aggregate counters shown above the source table.
//
// `readyForWatermarks` / `readyForDcp` count rows that the operator
// could legitimately encode against — flag is on AND QC didn't fail
// for that product. `needsAttention` is the operator's actionable
// counter: rows the upload pipeline either rejected or that need
// manual marker entry. Sources with `qc_running===true` aren't
// counted under any verdict bucket — they're still being measured.
export function computeSourceAggregates(files) {
  const list = Array.isArray(files) ? files : [];
  let totalBytes = 0;
  let readyForWatermarks = 0;
  let readyForDcp = 0;
  let needsAttention = 0;
  let running = 0;
  let bestCount = 0;
  for (const f of list) {
    if (!f) continue;
    totalBytes += Number(f.size) || 0;
    if (f.is_best) bestCount += 1;
    if (f.qc_running) {
      running += 1;
      continue;
    }
    const cpp = f.qc_compliance_per_product || {
      watermarks: f.qc_compliance,
      dcp: f.qc_compliance,
    };
    const wmOk = (f.available_for_watermark !== false) && cpp.watermarks !== 'fail';
    const dcpOk = (f.available_for_dcp !== false) && cpp.dcp !== 'fail' && cpp.dcp !== 'needs_input';
    if (wmOk) readyForWatermarks += 1;
    if (dcpOk) readyForDcp += 1;
    // "needs attention" = anything an operator should look at,
    // counted at most once per file.
    if (
      cpp.watermarks === 'fail' || cpp.dcp === 'fail' ||
      cpp.watermarks === 'needs_input' || cpp.dcp === 'needs_input'
    ) {
      needsAttention += 1;
    }
  }
  return {
    count: list.length,
    totalBytes,
    readyForWatermarks,
    readyForDcp,
    needsAttention,
    running,
    bestCount,
  };
}

// Should we show the operator a "QC said no, are you sure?" confirm
// before letting them flip the Available For switch ON for a given
// product? Returns the failing verdict ('fail' / 'needs_input') so
// the dialog can mention which signal it's overriding, or null if
// no warning is needed (verdict pass/warn or QC not yet run).
//
// Off→on direction only — turning a source OFF for a product never
// requires confirmation; the operator is opting out, the system
// shouldn't get in the way.
export function qcVerdictWarning(file, product) {
  if (!file) return null;
  // QC still running → no verdict yet, don't gate the toggle.
  if (file.qc_running) return null;
  const cpp = file.qc_compliance_per_product || {
    watermarks: file.qc_compliance,
    dcp: file.qc_compliance,
  };
  const verdict = cpp[product];
  if (verdict === 'fail') return 'fail';
  // needs_input is DCP-only (FFEC/FFMC missing); for watermarks the
  // marker concept doesn't apply, so treat as no-warning.
  if (product === 'dcp' && verdict === 'needs_input') return 'needs_input';
  return null;
}

// "Can the operator create a watermark / DCP job from this source?"
// Used to gate the create-job buttons in the QC drawer. We allow:
//   - the per-product Available For toggle is ON (or unset = ON), AND
//   - the per-product QC verdict isn't a hard fail. `needs_input`
//     blocks DCP creation (FFEC/FFMC must be supplied first); for
//     watermarks it doesn't apply (they don't carry markers).
//
// `qcReport` is the .source-qc.json report object — falsy means
// QC hasn't run yet, in which case we still allow the create
// (the post-finish hook may not have completed; better to let
// the operator try than to block them on a missing sidecar).
export function canCreateForProduct(qcReport, product, availableForFlags = {}) {
  const flagOn = availableForFlags[product] !== false;
  if (!flagOn) return false;
  if (!qcReport) return true; // QC not yet run — don't block
  const cpp = qcReport.compliance_per_product || {
    watermarks: qcReport.compliance,
    dcp: qcReport.compliance,
  };
  const verdict = cpp[product];
  if (verdict === 'fail') return false;
  if (product === 'dcp' && verdict === 'needs_input') return false;
  return true;
}

// Bulk-operations affordance gating.
//
// We don't want to show checkboxes + a multi-action bar to a
// brand-new operator with three sources — it adds visual noise the
// page doesn't earn. Once the workspace has a meaningful number of
// sources (10+), the affordance appears automatically. Superadmins
// get a "Force show bulk ops" override (persisted in localStorage)
// so they can QC the bulk-ops UI on a small fixture workspace.
export const BULK_OPS_THRESHOLD = 10;
export const BULK_OPS_FORCE_KEY = 'sources_bulk_force_visible';

export function shouldShowBulkOps(sourceCount, isSuperadmin, forceVisible) {
  if (sourceCount >= BULK_OPS_THRESHOLD) return true;
  if (isSuperadmin && forceVisible) return true;
  return false;
}

export function isBulkOpsForced() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(BULK_OPS_FORCE_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function setBulkOpsForced(forced) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (forced) window.localStorage.setItem(BULK_OPS_FORCE_KEY, '1');
    else window.localStorage.removeItem(BULK_OPS_FORCE_KEY);
  } catch (_e) {
    // Same fail-open semantics as the guide-dismiss flag.
  }
}

// Filter the source list by the operator's active criteria.
//
// `criteria.search`     — case-insensitive substring of the filename.
// `criteria.compliance` — one of 'all' / 'pass' / 'warn' / 'fail' /
//                         'needs_input' / 'running'. Matches on EITHER
//                         per-product verdict (DCP-only fail still
//                         counts as "fail"), since the operator's
//                         intent with the filter is "show me the
//                         attention-needers."
// `criteria.availableFor` — 'all' / 'watermarks' / 'dcp'. The flag
//                           must be ON for the chosen product (off =
//                           the operator turned it off intentionally,
//                           don't show it in that view).
//
// Empty / unset criteria all default to "show everything." Returns
// the same list (in the same order) the caller passed in.
export function filterSources(files, criteria = {}) {
  const list = Array.isArray(files) ? files : [];
  const search = (criteria.search || '').trim().toLowerCase();
  const compliance = criteria.compliance || 'all';
  const availableFor = criteria.availableFor || 'all';

  return list.filter((f) => {
    if (!f) return false;

    if (search && !(f.filename || '').toLowerCase().includes(search)) return false;

    if (compliance !== 'all') {
      if (compliance === 'running') {
        if (!f.qc_running) return false;
      } else {
        if (f.qc_running) return false; // running rows aren't a verdict
        const cpp = f.qc_compliance_per_product || {
          watermarks: f.qc_compliance,
          dcp: f.qc_compliance,
        };
        const matches = cpp.watermarks === compliance || cpp.dcp === compliance;
        if (!matches) return false;
      }
    }

    if (availableFor === 'watermarks' && f.available_for_watermark === false) return false;
    if (availableFor === 'dcp' && f.available_for_dcp === false) return false;

    return true;
  });
}

// Human-readable byte string. Mirrors the page's local fmtBytes but
// is exported so the aggregate-stat bar and tests can reuse it.
export function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
